import React from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from 'apollo-react/components/CircularProgress';

/**
 * TranslationReady Component
 *
 * Ensures translations are loaded before rendering children.
 * This prevents race conditions where components render with translation keys
 * instead of translated text.
 *
 * Best Practices (per react-i18next docs):
 * - Uses `ready` state from useTranslation hook (since useSuspense: false in i18n config)
 * - Language change re-renders are handled via key={i18n.language} on <Outlet> in layouts
 * - Avoids React.cloneElement anti-pattern (per React docs)
 *
 * Usage:
 * - Wrap layout content with this component
 * - Child components don't need to check 'ready' state individually
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when translations are ready
 * @param {boolean} props.fullScreen - Whether to show full-screen loader (default: true)
 */
const TranslationReady = ({ children, fullScreen = true }) => {
  const { ready } = useTranslation();

  if (!ready) {
    if (fullScreen) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  // Simply render children - language change re-renders handled via key on <Outlet>
  return <>{children}</>;
};

export default TranslationReady;
